/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Button,
  ButtonGroup,
  Dialog,
  InlineNotification,
  LoadingSection,
  SelectField,
  SelectOption,
} from '@coursera/cds-core';

import { useContractsByOrgId } from 'bundles/enterprise-admin-program-catalog/hooks/useContractsByOrgId';
import { getUtilization } from 'bundles/enterprise-admin-program-creation/utils/contract';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  selectField: css`
    width: 100%;
  `,
};

export type ContractSelectionStepProps = {
  open: boolean;
  onBack: () => void;
  onSelect: (contractId: string) => void;
};

/**
 * Shown once per invite flow, between method selection and the email/CSV entry
 * screen, for orgs with more than one contract (`InviteLearnersModal` owns the
 * skip decision for single/no-contract orgs). Selecting a contract lets a later
 * roster lookup scope the invite mutation to the right roster.
 */
const ContractSelectionStep = ({ open, onBack, onSelect }: ContractSelectionStepProps) => {
  const { contractOptions, error } = useContractsByOrgId();
  const [selectedContractId, setSelectedContractId] = React.useState<string | undefined>(undefined);

  if (!contractOptions) {
    return (
      <Dialog open={open} onClose={onBack} width="large">
        <HeadingGroup>{_t('Choose a contract')}</HeadingGroup>
        <Content>
          {/* Always-present live region so screen readers announce the failure when it appears. */}
          <div role="alert">
            {error && (
              <InlineNotification severity="error">
                {_t("We couldn't load your organization's contracts. Please try again.")}
              </InlineNotification>
            )}
          </div>
          {!error && <LoadingSection label={_t('Loading contracts…')} />}
        </Content>
        <Actions>
          <Button variant="secondary" onClick={onBack}>
            {_t('Back')}
          </Button>
        </Actions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onBack} width="large">
      <HeadingGroup>{_t('Choose a contract')}</HeadingGroup>
      <Content>
        <SelectField
          label={_t('Contract')}
          placeholder={_t('Select a contract')}
          value={selectedContractId ?? ''}
          onChange={(event) => setSelectedContractId(String(event.target.value))}
          css={styles.selectField}
        >
          {contractOptions.map((contract) => (
            <SelectOption key={contract.id} value={contract.id}>
              {`${contract.name} ${getUtilization({ limitType: contract.limitType ?? '', utilization: contract.utilization })}`}
            </SelectOption>
          ))}
        </SelectField>
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: _t('Next'),
            disabled: !selectedContractId,
            onClick: () => selectedContractId && onSelect(selectedContractId),
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: _t('Back'),
            onClick: onBack,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default ContractSelectionStep;
